import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { SmallText, Spacer } from "../../components/core/utility";
import { Loader } from "@vds/loaders";
import { Icon } from "@vds/icons";
import { Accordion, AccordionTab } from "primereact/accordion";

const BundleWrapper = styled.div`
  margin-top: 1rem;
`;
const BundleHeader = styled.div`
  background: #000;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 2rem;
`;
const VehicleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 12px;
  table-layout: fixed;
  th,
  td {
    padding: 4px 6px;
    vertical-align: top;
  }
  th {
    background: #f8f9fa;
    font-weight: 600;
    text-align: left;
  }
  tbody tr:nth-of-type(even) {
    background: #fafafa;
  }
`;
const VehicleCell = styled.td`
  font-weight: normal;
`;

const AccordionStyleOverride = styled.div`
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header:hover .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link {
    color: #000000;
    padding: 0.5rem;
    background: lightgray;
    border-radius: 10px !important;
    margin-bottom: 0.6rem;
    font-family: Verizon-NHG-eDS;
    font-size: 0.8rem;
    font-weight: 500;
    display: flex;
    align-items: center;
  }
  .p-accordion .p-accordion-content {
    padding: 0rem;
    padding-bottom: 0.8rem;
  }
  .p-accordion .p-accordion-header .p-accordion-toggle-icon {
    order: 2;
    margin-left: auto;
  }
`;

export const AssignMaterialVehicleGrid = ({
  bundleIds = [],
  addressId = "",
  contactId = "",
  crmRefId = "",
  materialFilter = {},
  vehicleList = [],
  activeBundleId = "",
  materialKey = "",
  externalSelectedVehicleIds = [],
  onSelectionChange = () => {},
  selectionLocked = false,
  openAllAccordions = false,
  selectionHighlight = false,
  showDeleteForSelected = false,
  hideSelectionColumn = false,
  onDeleteSelectedMapping = null,
  // New: provide bundle name mapping from parent to avoid reading sessionStorage here
  bundleNameMap = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vehiclesByBundle, setVehiclesByBundle] = useState({});
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [activeAddressIndex, setActiveAddressIndex] = useState({});

  // Removed sessionStorage access for CSPDetails to avoid exposing PII in this component
  // Use provided bundleNameMap prop for display
  const resolveBundleDisplay = (bundleId) => {
    const name = bundleNameMap[bundleId];
    return name ? `${name} (${bundleId})` : bundleId;
  };

  useEffect(() => {
    setSelectedVehicleIds(new Set(externalSelectedVehicleIds || []));
  }, [materialKey, externalSelectedVehicleIds]);

  const getVehicleKey = (selVehicle, fallbackKey) => {
    if (!selVehicle) return fallbackKey;
    const stableId = selVehicle?._stableId;
    const vinOrVehicle = selVehicle?.vin || selVehicle?.vehicle;
    const composed = `${selVehicle.year || "?"}-${selVehicle.make || "?"}-${selVehicle.model || "?"}-${selVehicle.licensePlate || "?"}`;
    return selVehicle?.id || vinOrVehicle || stableId || composed || fallbackKey;
  };

  const toggleVehicleSelected = (vehicle, providedKey) => {
    if (selectionLocked) return;
    const vehicleKey = providedKey || getVehicleKey(vehicle, undefined);
    setSelectedVehicleIds((previousSelected) => {
      const updatedSelected = new Set(previousSelected);
      if (updatedSelected.has(vehicleKey)) {
        updatedSelected.delete(vehicleKey);
      } else {
        updatedSelected.add(vehicleKey);
      }
      onSelectionChange({
        materialKey,
        hardwareId: materialFilter.hardwareId,
        subscriptionId: materialFilter.subscriptionId,
        techRefId: materialFilter.techRefId,
        vehicleIds: Array.from(updatedSelected),
      });
      return updatedSelected;
    });
  };

  useEffect(() => {
    if (vehicleList && !Array.isArray(vehicleList) && Object.keys(vehicleList).length) {
      setVehiclesByBundle(vehicleList);
      setLoading(false);
      setError("");
      return;
    }
    if (Array.isArray(vehicleList) && vehicleList.length) {
      const grouped = {};
      vehicleList.forEach((v) => {
        const bId = v.bundleId || activeBundleId || bundleIds[0] || "";
        if (!grouped[bId]) grouped[bId] = [];
        grouped[bId].push(v);
      });
      setVehiclesByBundle(grouped);
      setLoading(false);
      setError("");
      return;
    }
    if (!bundleIds.length) return;
  }, [
    vehicleList,
    activeBundleId,
    bundleIds,
    addressId,
    contactId,
    crmRefId,
    materialFilter.hardwareId,
    materialFilter.subscriptionId,
    materialFilter.techRefId,
  ]);

  const handleAddressAccordionChange = (bundleId) => (e) => {
    setActiveAddressIndex((prev) => ({ ...prev, [bundleId]: e.index }));
  };

  const renderVehicleCell = (row) => {
    const priorityLabel = row.priority ? `Hardware P${row.priority}` : "Hardware --";
    const hasMappings = Array.isArray(row.materialMapping) && row.materialMapping.length > 0;
    const allEnabled = hasMappings && row.materialMapping.every((m) => m.enabled !== false);
    const availabilityLabel = hasMappings && allEnabled ? "Hardware All" : "Missing Hardware";
    return (
      <div>
        <SmallText>
          {row.year} {row.make} {row.model}
        </SmallText>
        {row.displayVinId && <SmallText bold>{row.vehicle}</SmallText>}
        <SmallText css={{ marginTop: "4px" }} bold>
          {row.sourceApplication}
        </SmallText>
        <SmallText css={{ marginTop: "2px" }}>{priorityLabel}</SmallText>
        <SmallText css={{ marginTop: "2px" }}>{availabilityLabel}</SmallText>
        {row.isShippingBlocked && <SmallText css={{ marginTop: "2px" }}>Do Not Ship</SmallText>}
      </div>
    );
  };

  const renderTraits = (row) => (
    <div>
      <div>
        {row.wheelChair && (
          <Icon
            className="trash-icon"
            aria-label="InternationalSymbolOfAccess"
            alt="InternationalSymbolOfAccess"
            name="international-symbol-of-access"
            size="XLarge"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
            color="#000000"
          />
        )}
        {row.emergency && (
          <Icon
            className="police-fleet"
            aria-label="police-fleet"
            alt="police-fleet"
            name="police-fleet"
            size="Large"
            color="#000000"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
          />
        )}
        {row.killSwitch && (
          <Icon
            className="smart-switch"
            aria-label="smart-switch"
            alt="smart-switch"
            name="smart-switch"
            size="Large"
            color="#000000"
            style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
          />
        )}
      </div>
      <SmallText>{row.weightClass}</SmallText>
    </div>
  );

  if (loading) return <Loader active />;
  if (error && !Object.keys(vehiclesByBundle).length) return <SmallText>{error}</SmallText>;
  if (!loading && vehicleList === null) {
    return <SmallText>No data to display</SmallText>;
  }

  return (
    <>
      {Object.keys(vehiclesByBundle).map((bundleId) => {
        const dataArr = vehiclesByBundle[bundleId];
        if (!Array.isArray(dataArr) || !dataArr.length) return null;
        const isAddressGroupShape = dataArr.some((item) => item && typeof item === "object" && Array.isArray(item.vehicles));

        // compute total columns for dynamic colSpan
        const totalColumns = 12 + (showDeleteForSelected ? 1 : 0) - (hideSelectionColumn ? 1 : 0);

        if (isAddressGroupShape) {
          return (
            <BundleWrapper key={bundleId}>
              <BundleHeader>{resolveBundleDisplay(bundleId)}</BundleHeader>
              <AccordionStyleOverride>
                <Accordion
                  expandIcon="pi pi-plus"
                  collapseIcon="pi pi-minus"
                  multiple={openAllAccordions}
                  activeIndex={openAllAccordions ? dataArr.map((_, idx) => idx) : activeAddressIndex[bundleId]}
                  onTabChange={handleAddressAccordionChange(bundleId)}
                >
                  {dataArr.map((group, gIdx) => {
                    const address = group.address || {};
                    const contact = group.contact || {};
                    const vehicles = Array.isArray(group.vehicles) ? group.vehicles : [];
                    const contactName = contact.name || contact.contact || contact.contactName || "";
                    const streetLine = address.address || address.address1 || "";
                    const addr2Part = address.address2 || address.addressTwo ? `, ${address.address2 || address.addressTwo}` : "";
                    const cityPart = address.city ? `, ${address.city}` : "";
                    const statePart = address.stateCode || address.state ? `, ${address.stateCode || address.state}` : "";
                    const zipPart = address.zipcode || address.zipCode ? ` ${address.zipcode || address.zipCode}` : "";
                    const addressLabel = streetLine ? `${streetLine}${addr2Part}${cityPart}${statePart}${zipPart}` : `Address-${gIdx + 1}`;
                    return (
                      <AccordionTab
                        key={gIdx}
                        header={
                          <div style={{ fontSize: "14px" }}>
                            <b>{addressLabel}</b>
                            {contactName && (
                              <span>
                                {" - "}
                                <b>{contactName}</b>
                              </span>
                            )}
                          </div>
                        }
                      >
                        <VehicleTable>
                          <tbody>
                            <tr style={{ background: "#f8f9fa" }}>
                              {!hideSelectionColumn && <td style={{ width: "4%" }}></td>}
                              <td style={{ width: hideSelectionColumn ? "22%" : "18%" }}>
                                <SmallText bold>Vehicle</SmallText>
                              </td>
                              <td style={{ width: "6%" }}>
                                <SmallText bold>Traits</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Category</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Sub-Category</SmallText>
                              </td>
                              <td style={{ width: "14%" }}>
                                <SmallText bold>Subscription</SmallText>
                              </td>
                              <td style={{ width: "13%" }}>
                                <SmallText bold>Tech-Ref Id</SmallText>
                              </td>
                              <td style={{ width: "7%" }}>
                                <SmallText bold>Line Type</SmallText>
                              </td>
                              <td style={{ width: "15%" }}>
                                <SmallText bold>Hardware</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Work Ticket</SmallText>
                              </td>
                              <td style={{ width: "8%" }}>
                                <SmallText bold>Work Ticket Status</SmallText>
                              </td>
                              <td style={{ width: "7%" }}>
                                <SmallText bold>Ticket Type</SmallText>
                              </td>
                              {showDeleteForSelected && <td style={{ width: "4%" }}></td>}
                            </tr>
                            {vehicles.length === 0 && (
                              <tr>
                                <td colSpan={totalColumns}>
                                  <SmallText>No vehicles for this address</SmallText>
                                </td>
                              </tr>
                            )}
                            {vehicles.map((vehicle, vehicleIndex) => {
                              const materialMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
                              const fallbackKey = `${bundleId}-${gIdx}-${vehicleIndex}`;
                              const vehicleKey = getVehicleKey(vehicle, fallbackKey);
                              const isEvenRow = vehicleIndex % 2 === 1;
                              const rowBackground = isEvenRow ? "rgb(240,240,240)" : "white";
                              if (!materialMappings.length) {
                                const remainderColSpan = totalColumns - (hideSelectionColumn ? 2 : 3);
                                return (
                                  <tr key={vehicleKey} style={{ backgroundColor: rowBackground }}>
                                    {!hideSelectionColumn && (
                                      <td>
                                        <input
                                          type="checkbox"
                                          aria-label="Select vehicle"
                                          checked={selectedVehicleIds.has(vehicleKey)}
                                          onChange={() => toggleVehicleSelected(vehicle, vehicleKey)}
                                          disabled={selectionLocked}
                                        />
                                      </td>
                                    )}
                                    <VehicleCell>{renderVehicleCell(vehicle)}</VehicleCell>
                                    <td>{renderTraits(vehicle)}</td>
                                    <td colSpan={remainderColSpan}>
                                      <SmallText>No hardware mapping</SmallText>
                                    </td>
                                  </tr>
                                );
                              }
                              return materialMappings.map((mapping, mappingIndex) => (
                                <tr key={`${vehicleKey}-${mappingIndex}`} style={{ backgroundColor: rowBackground }}>
                                  {mappingIndex === 0 && (
                                    <>
                                      {!hideSelectionColumn && (
                                        <td rowSpan={materialMappings.length}>
                                          <input
                                            type="checkbox"
                                            aria-label="Select vehicle"
                                            checked={selectedVehicleIds.has(vehicleKey)}
                                            onChange={() => toggleVehicleSelected(vehicle, vehicleKey)}
                                            disabled={selectionLocked}
                                          />
                                        </td>
                                      )}
                                      <VehicleCell rowSpan={materialMappings.length}>{renderVehicleCell(vehicle)}</VehicleCell>
                                      <td rowSpan={materialMappings.length}>{renderTraits(vehicle)}</td>
                                    </>
                                  )}
                                  {(() => {
                                    const colorStyle = selectionHighlight ? { color: mapping._isSelected ? "#000" : "#9e9e9e" } : {};
                                    return (
                                      <>
                                        <td style={colorStyle}>{mapping.type || "--"}</td>
                                        <td style={colorStyle}>{mapping.subType || "--"}</td>
                                        <td style={colorStyle}>
                                          {mapping.subscriptionName ? `${mapping.subscriptionName} (${mapping.subscriptionId})` : "--"}
                                        </td>
                                        <td style={colorStyle}>{mapping.techRefId || "--"}</td>
                                        <td style={colorStyle}>{mapping.lineType || mapping.orderType || "--"}</td>
                                        <td style={colorStyle}>{mapping.hardwareName ? `${mapping.hardwareName} (${mapping.hardwareId})` : "--"}</td>
                                        <td style={colorStyle}>{mapping.workticketId || "--"}</td>
                                        <td style={colorStyle}>{mapping.workticketStatus || "--"}</td>
                                        <td style={colorStyle}>{mapping.workticketType || "--"}</td>
                                        {showDeleteForSelected && (
                                          <td>
                                            {selectionHighlight && mapping._isSelected ? (
                                              <div
                                                role="button"
                                                tabIndex={0}
                                                onMouseDown={(e) => {
                                                  e && e.preventDefault && e.preventDefault();
                                                  e && e.stopPropagation && e.stopPropagation();
                                                  if (e && e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
                                                    e.nativeEvent.stopImmediatePropagation();
                                                  }
                                                }}
                                                onClick={(e) => {
                                                  e && e.preventDefault && e.preventDefault();
                                                  e && e.stopPropagation && e.stopPropagation();
                                                  if (e && e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
                                                    e.nativeEvent.stopImmediatePropagation();
                                                  }
                                                  onDeleteSelectedMapping && onDeleteSelectedMapping(vehicle, mapping);
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    onDeleteSelectedMapping && onDeleteSelectedMapping(vehicle, mapping);
                                                  }
                                                }}
                                                style={{ cursor: "pointer", display: "inline-flex" }}
                                              >
                                                <Icon
                                                  className="trash-icon"
                                                  aria-label="Delete"
                                                  alt="delete-icon"
                                                  name="trash"
                                                  size="medium"
                                                  color="#000000"
                                                  style={{ outline: "none" }}
                                                />
                                              </div>
                                            ) : (
                                              <span />
                                            )}
                                          </td>
                                        )}
                                      </>
                                    );
                                  })()}
                                </tr>
                              ));
                            })}
                          </tbody>
                        </VehicleTable>
                      </AccordionTab>
                    );
                  })}
                </Accordion>
              </AccordionStyleOverride>
              <Spacer top="1rem" />
            </BundleWrapper>
          );
        }

        const vehs = dataArr;
        const groupedByAddress = vehs.reduce((acc, v) => {
          const addr = v.address || {};
          const key = addr.addressId || addr.address1 || "UNKNOWN";
          if (!acc[key]) acc[key] = { address: addr, vehicles: [] };
          acc[key].vehicles.push(v);
          return acc;
        }, {});
        return (
          <BundleWrapper key={bundleId}>
            <BundleHeader>{resolveBundleDisplay(bundleId)}</BundleHeader>
            <AccordionStyleOverride>
              <Accordion
                expandIcon="pi pi-plus"
                collapseIcon="pi pi-minus"
                multiple={openAllAccordions}
                activeIndex={openAllAccordions ? Object.keys(groupedByAddress).map((_, idx) => idx) : activeAddressIndex[bundleId]}
                onTabChange={handleAddressAccordionChange(bundleId)}
              >
                {Object.keys(groupedByAddress).map((addrKey) => {
                  const { address, vehicles } = groupedByAddress[addrKey];
                  const buildAddress = (a) => {
                    const streetLine = a.address || a.address1 || "";
                    if (!streetLine) return addrKey;
                    const parts = [streetLine];
                    if (a.address2 || a.addressTwo) parts.push(a.address2 || a.addressTwo);
                    const tail = [];
                    if (a.city) tail.push(a.city);
                    if (a.stateCode || a.state) tail.push(a.stateCode || a.state);
                    if (a.zipcode || a.zipCode) tail.push(a.zipcode || a.zipCode);
                    if (tail.length) parts.push(tail.join(", "));
                    return parts.join(", ");
                  };
                  const addressLabel = buildAddress(address);
                  return (
                    <AccordionTab
                      key={addrKey}
                      header={
                        <div style={{ fontSize: "14px" }}>
                          <b>{addressLabel}</b>
                        </div>
                      }
                    >
                      <VehicleTable>
                        <tbody>
                          <tr style={{ background: "#f8f9fa" }}>
                            {!hideSelectionColumn && <td style={{ width: "4%" }}></td>}
                            <td style={{ width: hideSelectionColumn ? "22%" : "18%" }}>
                              <SmallText bold>Vehicle</SmallText>
                            </td>
                            <td style={{ width: "6%" }}>
                              <SmallText bold>Traits</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Category</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Sub-Category</SmallText>
                            </td>
                            <td style={{ width: "14%" }}>
                              <SmallText bold>Subscription</SmallText>
                            </td>
                            <td style={{ width: "13%" }}>
                              <SmallText bold>Tech-Ref Id</SmallText>
                            </td>
                            <td style={{ width: "7%" }}>
                              <SmallText bold>Line Type</SmallText>
                            </td>
                            <td style={{ width: "15%" }}>
                              <SmallText bold>Hardware</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Work Ticket</SmallText>
                            </td>
                            <td style={{ width: "8%" }}>
                              <SmallText bold>Work Ticket Status</SmallText>
                            </td>
                            <td style={{ width: "7%" }}>
                              <SmallText bold>Ticket Type</SmallText>
                            </td>
                            {showDeleteForSelected && <td style={{ width: "4%" }}></td>}
                          </tr>
                          {vehicles.map((vehicle, vehicleIndex) => {
                            const materialMappings = Array.isArray(vehicle.materialMapping) ? vehicle.materialMapping : [];
                            const fallbackKey = `${bundleId}-${addrKey}-${vehicleIndex}`;
                            const vehicleKey = getVehicleKey(vehicle, fallbackKey);
                            const isEvenVehicleRow = vehicleIndex % 2 === 1;
                            const groupBackgroundColor = isEvenVehicleRow ? "rgb(240, 240, 240)" : "white";
                            if (!materialMappings.length) {
                              const remainderColSpan = totalColumns - (hideSelectionColumn ? 2 : 3);
                              return (
                                <tr key={vehicleKey} style={{ backgroundColor: groupBackgroundColor }}>
                                  {!hideSelectionColumn && (
                                    <td>
                                      <input
                                        type="checkbox"
                                        aria-label="Select vehicle"
                                        checked={selectedVehicleIds.has(vehicleKey)}
                                        onChange={() => toggleVehicleSelected(vehicle, vehicleKey)}
                                        disabled={selectionLocked}
                                      />
                                    </td>
                                  )}
                                  <VehicleCell>{renderVehicleCell(vehicle)}</VehicleCell>
                                  <td>{renderTraits(vehicle)}</td>
                                  <td colSpan={remainderColSpan}>
                                    <SmallText>No hardware mapping</SmallText>
                                  </td>
                                </tr>
                              );
                            }
                            return materialMappings.map((mapping, mappingIndex) => (
                              <tr key={`${vehicleKey}-${mappingIndex}`} style={{ backgroundColor: groupBackgroundColor }}>
                                {mappingIndex === 0 && (
                                  <>
                                    {!hideSelectionColumn && (
                                      <td rowSpan={materialMappings.length}>
                                        <input
                                          type="checkbox"
                                          aria-label="Select vehicle"
                                          checked={selectedVehicleIds.has(vehicleKey)}
                                          onChange={() => toggleVehicleSelected(vehicle, vehicleKey)}
                                          disabled={selectionLocked}
                                        />
                                      </td>
                                    )}
                                    <VehicleCell rowSpan={materialMappings.length}>{renderVehicleCell(vehicle)}</VehicleCell>
                                    <td rowSpan={materialMappings.length}>{renderTraits(vehicle)}</td>
                                  </>
                                )}
                                {(() => {
                                  const colorStyle = selectionHighlight ? { color: mapping._isSelected ? "#000" : "#9e9e9e" } : {};
                                  return (
                                    <>
                                      <td style={colorStyle}>{mapping.type || "--"}</td>
                                      <td style={colorStyle}>{mapping.subType || "--"}</td>
                                      <td style={colorStyle}>
                                        {mapping.subscriptionName ? `${mapping.subscriptionName} (${mapping.subscriptionId})` : "--"}
                                      </td>
                                      <td style={colorStyle}>{mapping.techRefId || "--"}</td>
                                      <td style={colorStyle}>{mapping.lineType || mapping.orderType || "--"}</td>
                                      <td style={colorStyle}>
                                        {mapping.hardwareName ? `${mapping.hardwareName} (${mapping.hardwareId})` : "--"}
                                        {/* {selectionHighlight && mapping._isSelected ? " ×" : ""} */}
                                      </td>
                                      <td style={colorStyle}>{mapping.workTicketId || "--"}</td>
                                      <td style={colorStyle}>{mapping.workticketStatus || mapping.processingCode || "--"}</td>
                                      <td style={colorStyle}>
                                        {mapping.ticketType || (mapping.upsell !== undefined ? (mapping.upsell ? "Upsell" : "Standard") : "--")}
                                      </td>
                                      {showDeleteForSelected && (
                                        <td>
                                          {selectionHighlight && mapping._isSelected ? (
                                            <div
                                              role="button"
                                              tabIndex={0}
                                              onMouseDown={(e) => {
                                                e && e.preventDefault && e.preventDefault();
                                                e && e.stopPropagation && e.stopPropagation();
                                                if (e && e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
                                                  e.nativeEvent.stopImmediatePropagation();
                                                }
                                              }}
                                              onClick={(e) => {
                                                e && e.preventDefault && e.preventDefault();
                                                e && e.stopPropagation && e.stopPropagation();
                                                if (e && e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === "function") {
                                                  e.nativeEvent.stopImmediatePropagation();
                                                }
                                                onDeleteSelectedMapping && onDeleteSelectedMapping(vehicle, mapping);
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                  e.preventDefault();
                                                  onDeleteSelectedMapping && onDeleteSelectedMapping(vehicle, mapping);
                                                }
                                              }}
                                              style={{ cursor: "pointer", display: "inline-flex" }}
                                            >
                                              <Icon
                                                className="trash-icon"
                                                aria-label="Delete"
                                                alt="delete-icon"
                                                name="trash"
                                                size="medium"
                                                color="#000000"
                                                style={{ outline: "none" }}
                                              />
                                            </div>
                                          ) : (
                                            <span />
                                          )}
                                        </td>
                                      )}
                                    </>
                                  );
                                })()}
                              </tr>
                            ));
                          })}
                        </tbody>
                      </VehicleTable>
                    </AccordionTab>
                  );
                })}
              </Accordion>
            </AccordionStyleOverride>
            <Spacer top="1rem" />
          </BundleWrapper>
        );
      })}
      {Object.keys(vehiclesByBundle).length > 0 && Object.keys(vehiclesByBundle).every((bid) => !vehiclesByBundle[bid]?.length) ? (
        <SmallText>No matching vehicles for selected material</SmallText>
      ) : null}
      {Object.keys(vehiclesByBundle).length === 0 && vehicleList !== null && !loading && <SmallText>No data to display</SmallText>}
    </>
  );
};

export const AssignMaterialVehicleGridMetadata = {
  name: "Assign Material Vehicles",
  id: "assign_material_vehicle_grid",
  component: React.lazy(() => import("./AssignMaterialVehicleGrid")),
  route: "/connect360/assign_material_vehicle_grid",
  Breadcrumbs: true,
};

export default AssignMaterialVehicleGrid;
